import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import PullOutCard from './pull-out-card/pull-out-card';
import PullOutMoreCard from './pull-out-more-card/pull-out-more-card';
import { collectionSerializer } from '@/providers/serializers/content/collection';
import { assessmentSerializer } from '@/providers/serializers/content/assessment';
import { profileAPI } from '@/providers/apis/profile/profile';
import { contentAPI } from '@/providers/apis/content/content';
import { resourceSerializer } from '@/providers/serializers/content/resource';
import { questionSerializer } from '@/providers/serializers/content/question';
import { EXTRACTED, CURATED, TAGEED, COMPUTED } from '@/utils/constants';
import axios from 'axios';
import { courseSerializer } from '@/providers/serializers/content/course';
import { lookupAPI } from '@/providers/apis/lookup/lookup';
import { lessonSerializer } from '@/providers/serializers/content/lesson';
import { markersAPI } from '@/providers/apis/markers/markers';

@Component({
    name: 'catalog-pull-out',
    components: {
        'material-icon': GoogleMaterialIcon,
        'pull-out': PullOutCard,
        'pull-out-more': PullOutMoreCard,
    },
})

export default class CatalogPullOut extends Vue {

    // ------------------------------------------------------------------
    // Properties

    @Prop()
    private isPullOutLoading!: boolean;

    @Prop()
    private collectionData: any;

    private contentData: any = [];

    private isShowMore: boolean = false;

    private pullOutMoreData: any = null;

    private isLoading: boolean = false;

    private metaData: any = {};

    private dokContent: any = [];

    private readingLevel: any = [];

    private headerMenus: any = [
        {
            name: 'Extracted',
            isActive: true,
            color: '#0072bc',
            keyList: EXTRACTED,
        },
        {
            name: 'Curated',
            isActive: true,
            color: '#28a745',
            keyList: CURATED,
        },
        {
            name: 'Tagged',
            isActive: true,
            color: '#fd7e14',
            keyList: TAGEED,
        },
        {
            name: 'Computed',
            isActive: true,
            color: '#37424b',
            keyList: COMPUTED,
        },
    ];

    // --------------------------------------------------
    // Actions

    /**
     * Use to show more content on pull out
     */
    public onShowMore() {
        this.isShowMore = !this.isShowMore;
        (this.$el as HTMLElement).style.left = 'calc(100vw - 450px)';
        if (this.isShowMore) {
            (this.$el as any).style.left = 0;
        }
    }

    /**
     * Used to close pullout container
     */
    public closeContainer() {
        document.body.style.overflowY = 'auto';
        (this.$el as HTMLElement).style.left = '100vw';
        this.isShowMore = false;
    }

    /**
     * open pullout content using root actions
     */
    public openContainer() {
        (this.$el as HTMLElement).style.left = 'calc(100vw - 450px)';
    }
    // -----------------------------------------------------------------
    // Hooks
    private created() {
        contentAPI.getMetadataLevel().then((metaData) => {
            this.metaData = metaData;
        });
        this.fetchMetaData();
    }

    @Watch('collectionData', { deep: true })
    private watchingContent(value: any) {
        if (!this.isPullOutLoading) {
            this.onLoadContent(value);
        }

    }

    private onLoadContent(content: any) {
        this.isLoading = true;
        const contentType = content.type;
        if (contentType === 'collection') {
            if (content.lastModifiedBy) {
                axios.all([
                    this.getUserProfileData(content.lastModifiedBy),
                    contentAPI.getCollectionById(content.id),
                ]).then(axios.spread((userData, collectionContent) => {
                    this.isLoading = false;
                    this.contentData = collectionContent.content;
                    content.lastModifiedUser = userData[0];
                    this.pullOutMoreData = collectionSerializer
                        .collectionPulloutSerializer(content);
                }));
            }
        } else if (contentType === 'assessment') {
                    if (content.lastModifiedBy) {
                        axios.all([
                            this.getUserProfileData(content.lastModifiedBy),
                            contentAPI.fetchAssessment(content.id),
                        ]).then(axios.spread((userData, assessmentContent) => {
                            this.isLoading = false;
                            this.contentData = assessmentContent.question;
                            content.lastModifiedUser = userData[0];
                            this.pullOutMoreData = assessmentSerializer
                                .assessmentPulloutSerializer(content);
                        }));
                    }

        } else if (contentType === 'offline-activity') {
            if (content.lastModifiedBy) {
                axios.all([
                    this.getUserProfileData(content.lastModifiedBy),
                    contentAPI.getActivityById(content.id),
                ]).then(axios.spread((userData, oaData) => {
                    this.isLoading = false;
                    content.lastModifiedUser = userData[0];
                    this.contentData = oaData.oa_tasks;
                    // Currently we have use collection metadata format if we need any change
                    // We have to create seperate meta data serializer for offline activity
                    this.pullOutMoreData = collectionSerializer
                    .collectionPulloutSerializer(content);
                }));
            }

        } else if (contentType === 'course') {
            if (content.lastModifiedBy) {
                axios.all([
                    this.getUserProfileData(content.lastModifiedBy),
                    contentAPI.getCourseById(content.id),
                ]).then(axios.spread((userData, courseContent) => {
                    this.isLoading = false;
                    content.lastModifiedUser = userData[0];
                    this.contentData = [];
                    this.pullOutMoreData = courseSerializer
                        .coursePulloutSerializer(content, courseContent);
                }));
            }

        } else if (contentType === 'lesson') {
            if (content.lastModifiedBy) {
                axios.all([
                    this.getUserProfileData(content.lastModifiedBy) as any,
                    markersAPI.fetchRouteLessons({lessonId: content.id}),
                ]).then(axios.spread((userData, lessonContent: any) => {
                    this.isLoading = false;
                    content.lastModifiedUser = userData[0];
                    content.creator = content.owner;
                    this.contentData = lessonContent;
                    this.pullOutMoreData = lessonSerializer
                        .lessonPulloutSerializer(content, lessonContent);
                }));
            }

        } else if (contentType === 'resource') {
            axios.all([
                contentAPI.getResourceById(content.id),
                contentAPI.fetchSummary(content.id),
                contentAPI.fetchTranscript(content.id),
            ]).then(axios.spread((resourceContent, summary, transcript) => {
                this.parseMetaDataLevel(resourceContent);
                resourceContent.owner = content.owner;
                resourceContent.creator = content.creator;
                this.isLoading = false;
                this.contentData = [];
                this.pullOutMoreData = resourceSerializer
                    .resourcePulloutSerializer(resourceContent, content,
                        this.metaData, summary, transcript);
            }));

        } else if (contentType === 'question') {
            contentAPI.getQuestionById(content.id).then((questionData) => {
                this.parseMetaDataLevel(questionData);
                questionData.owner = content.owner;
                questionData.creator = content.creator;
                this.isLoading = false;
                this.contentData = [];
                this.pullOutMoreData = questionSerializer
                .questionPulloutSerializer(questionData,
                        content,
                        this.metaData);
            });
        }

    }

    private getUserProfileData(userId: string) {
        return profileAPI.fetchUserProfiles(userId)
            .then((userData) => {
                return userData;
            });
    }

    private parseMetaDataLevel(resourceContent: any) {
        if (resourceContent.metadata) {
            const metadata = resourceContent.metadata;
            const readingLevel = metadata.reading_level || [];
            const dok = metadata.depth_of_knowledge || [];
            resourceContent.readingLevel = this.readingLevel.filter(
                (item: any) => readingLevel.indexOf(item.id) !== -1);
            resourceContent.depthofknowledge = this.dokContent.filter(
                (item: any) => dok.indexOf(item.id) !== -1);
        }
    }

    private fetchMetaData() {
        axios.all([
            lookupAPI.readDepthOfKnowledgeItems(),
            lookupAPI.readingLevelItems(),
        ]).then(axios.spread((dok, readingLevel) => {
            this.dokContent = dok;
            this.readingLevel = readingLevel;
        }));
    }
}
